/**
 * What is permanently true about MPRO, and where each live number comes from.
 *
 * The token page used to show a tokenomics picture and nothing else — no
 * address, no supply, no burn fee, no emission. A service whose whole purpose
 * is managing a token, its distribution and its nodes was the one place a
 * holder could not look the token up. This module is the factual half of the
 * fix; `useMproTokenStats` reads the live half from chain.
 */

/**
 * MPRO is deployed at the same address on every chain it lives on
 * (deterministic deploy), which is worth stating on the card: an address that
 * differs per chain is the usual way people get phished.
 *
 * The card does not hardcode this list — it reads the chains from the
 * platform's own contract configuration, so a future deployment appears on the
 * card the moment it is configured, without a release.
 */
export const ETHEREUM_CHAIN_ID = 1
export const BASE_CHAIN_ID = 8453

/** The chain the token is minted on. Everywhere else holds bridged supply. */
export const MINTING_CHAIN_ID = ETHEREUM_CHAIN_ID

/** Master distributor — same address on both chains, immutable, not a proxy. */
export const MASTER_DISTRIBUTOR_ADDRESS = '0x568cd600A92b26f848804E4005822F7D28753632'

const EXPLORERS: Record<number, { name: string; url: string }> = {
  1: { name: 'Ethereum', url: 'https://etherscan.io' },
  56: { name: 'BNB Chain', url: 'https://bscscan.com' },
  137: { name: 'Polygon', url: 'https://polygonscan.com' },
  42161: { name: 'Arbitrum', url: 'https://arbiscan.io' },
  8453: { name: 'Base', url: 'https://basescan.org' }
}

export function chainName(chainId: number): string {
  return EXPLORERS[chainId]?.name ?? `Chain ${chainId}`
}

export function addressUrl(chainId: number, address: string): string {
  const explorer = EXPLORERS[chainId]?.url
  return explorer ? `${explorer}/token/${address}` : ''
}

/** Hard cap written into the token contract: it can never be raised. */
export const MAX_SUPPLY = 500_000_000

/**
 * Minted once, in the token's constructor on Ethereum, to a single address.
 * Base runs MPROLight, whose constructor mints nothing — every MPRO on Base
 * arrived over the LayerZero bridge. That is what makes the burn arithmetic
 * sound: bridging burns on one chain and mints on the other, so it cancels out
 * of a sum across chains, and what is left over is a real burn.
 *
 * Verified against the constructor arguments of the live contract
 * (`contract_logs/deployments_22_04-11:00/ethereum/MPRO.json`).
 */
export const PREMINT = 246_880_158

/**
 * Storage slot holding `distributedTokens` in the master distributor.
 *
 * The counter is `private`, so there is no getter — but the contract has no
 * proxy and no upgrade path, so its storage layout is fixed for good. The slot
 * is confirmed by its neighbours on the live contract: slot 6 returns exactly
 * what `distributionStartTimestamp()` returns and slot 9 exactly what
 * `burnRate()` returns, and `distributedTokens` is declared between them.
 *
 * Without it there is no honest way to say how much MPRO has ever been minted,
 * and therefore no way to say how much has been burned.
 */
export const DISTRIBUTED_TOKENS_SLOT = 7

export const MARKET_LINKS = [
  { label: 'CoinMarketCap', href: 'https://coinmarketcap.com/currencies/mpro-lab/' },
  { label: 'CoinGecko', href: 'https://www.coingecko.com/en/coins/mpro-lab' }
]

export function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`
}

/** Whole tokens, grouped — never more precision than a holder can use. */
export function formatTokens(value: number | null, fractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })
}
